/**
 * „Przypisane ankiety" — one bar under the range bar: the check-ins this client is enrolled in, each
 * a link to its form on `/raporty`, and „Przypisz ankietę" at the far end.
 *
 * Added 8 October 2026 with the assign dialog: an assignment opens no row in the history until its
 * first cycle is sent, so this is where the trainer SEES that it landed (the app has no toasts —
 * `features/attention-queue/lib/useTaskAct`). The mobile app lists them the same way.
 *
 * One row from the first frame in all four states, the chips scrolling sideways inside it, so the
 * answer changes ink and never geometry. It forks on the tab's gate, which waits for this read too.
 */

import { Link } from '@tanstack/react-router';
import { motion, type HTMLMotionProps } from 'motion/react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import chip from '~/shared/ui/neutralChip.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { EnrolledSurveysState } from '../lib/useEnrolledSurveys';
import eyebrows from './cardEyebrow.module.css';
import styles from './SurveysTab.module.css';

interface EnrolledSurveysBarProps {
  readonly state: EnrolledSurveysState;
  readonly gate: SkeletonGate;
  /** `null` until the card's header read has named the client. */
  readonly onAssignSurvey: ((opener: HTMLElement) => void) | null;
  readonly entrance: HTMLMotionProps<'section'>;
}

export function EnrolledSurveysBar({ state, gate, onAssignSurvey, entrance }: EnrolledSurveysBarProps) {
  const { t } = useTranslation();
  return (
    <motion.section
      className={styles.enrolledBar}
      aria-label={t('web:clientDetail.surveys.enrolledEyebrow')}
      aria-busy={gate.waiting}
      {...entrance}
    >
      <em className={`${String(eyebrows.eyebrow)} ${String(styles.enrolledEyebrow)}`}>
        {t('web:clientDetail.surveys.enrolledEyebrow')}
      </em>
      <div className={styles.enrolledList}>
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.surveys.enrolledLoading')}</span>
            {gate.skeleton ? (
              <i className={`${String(skel.bar)} ${String(styles.enrolledSkel)}`} aria-hidden="true" />
            ) : null}
          </>
        ) : state.failed || state.enrolled === null ? (
          <span className={`${String(styles.enrolledNote)} ${gate.reveal}`}>
            {t('web:clientDetail.surveys.enrolledFailed')}{' '}
            <button type="button" className={inlineButton.inlineTextButton} onClick={state.retry}>
              {t('web:clientDetail.states.retry')}
            </button>
          </span>
        ) : state.enrolled.length === 0 ? (
          <span className={`${String(styles.enrolledNote)} ${gate.reveal}`}>
            {t('web:clientDetail.surveys.enrolledNone')}
          </span>
        ) : (
          <ul className={`${String(styles.enrolledChips)} ${gate.reveal}`}>
            {state.enrolled.map((survey) => (
              <li key={survey.id}>
                <Link
                  to="/raporty/$templateId"
                  params={{ templateId: String(survey.id) }}
                  className={`${String(chip.neutralChip)} ${String(styles.enrolledChip)}`}
                >
                  <span className={styles.enrolledTitle}>{survey.title}</span>
                  {survey.cadence === null ? null : <span>{`· ${survey.cadence}`}</span>}
                  {survey.paused ? (
                    <span>{`· ${t('web:clientDetail.surveys.enrolledPaused')}`}</span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        className={`${String(ghost.ghost)} ${String(styles.assignBar)}`}
        aria-disabled={onAssignSurvey === null ? true : undefined}
        onClick={(event) => {
          onAssignSurvey?.(event.currentTarget);
        }}
      >
        {t('web:clientDetail.surveys.assign')}
      </button>
    </motion.section>
  );
}
